import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';
import { MessageSquareWarning, Plus } from 'lucide-react';

/**
 * Historial de PQR del usuario autenticado.
 * Consume GET /api/pqr/mine.
 */
const STATE_LABELS = {
  radicada: { label: 'Radicada', tone: 'bg-slate-100 text-slate-900' },
  en_revision: { label: 'En revisión', tone: 'bg-amber-100 text-amber-900' },
  en_gestion: { label: 'En gestión', tone: 'bg-blue-100 text-blue-900' },
  respondida: { label: 'Respondida', tone: 'bg-emerald-100 text-emerald-900' },
  cerrada: { label: 'Cerrada', tone: 'bg-slate-900 text-white' },
};

function Badge({ status }) {
  const cfg = STATE_LABELS[status] || { label: status || '—', tone: 'bg-slate-100 text-slate-700' };
  return (
    <span data-testid={`pqr-state-${status || 'unknown'}`} className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cfg.tone}`}>
      {cfg.label}
    </span>
  );
}

export default function MyPQR() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: 'Mis PQR | AutoMarket Colombia',
    description: 'Historial y estado de tus PQR radicadas en AutoMarket Colombia.',
    canonical: '/mis-pqr',
  });

  useEffect(() => {
    api
      .get('/pqr/mine', { params: { limit: 50 } })
      .then(({ data }) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <section className="border-b border-slate-200 bg-slate-900 text-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center bg-orange-500 text-slate-900">
            <MessageSquareWarning className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-400">Historial</div>
            <h1 className="font-display text-3xl font-black tracking-tighter">Mis PQR</h1>
          </div>
          <div className="flex-1" />
          <Link
            to="/pqr"
            data-testid="mypqr-new-btn"
            className="inline-flex items-center gap-2 bg-orange-500 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-orange-400"
          >
            <Plus className="h-4 w-4" /> Radicar nueva PQR
          </Link>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        {loading ? (
          <p className="text-center text-slate-500 py-12" data-testid="mypqr-loading">Cargando…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-slate-500 py-12" data-testid="mypqr-empty">
            Todavía no tienes PQR radicadas.
          </p>
        ) : (
          <div className="border border-slate-200" data-testid="mypqr-list">
            <div className="grid grid-cols-12 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <div className="col-span-3">Radicado</div>
              <div className="col-span-3">Fecha</div>
              <div className="col-span-4">Asunto</div>
              <div className="col-span-2 text-right">Estado</div>
            </div>
            {items.map((p) => (
              <div
                key={p.pqr_id}
                data-testid={`mypqr-row-${p.ticket_number || p.pqr_id}`}
                className="grid grid-cols-12 gap-3 border-b border-slate-100 px-4 py-3 text-sm hover:bg-slate-50"
              >
                <div className="col-span-3 font-mono text-xs text-slate-900">
                  {p.ticket_number || p.pqr_id}
                </div>
                <div className="col-span-3 text-slate-600">
                  {(p.created_at || '').substring(0, 10)}
                </div>
                <div className="col-span-4 text-slate-900">{p.subject}</div>
                <div className="col-span-2 text-right">
                  <Badge status={p.status} />
                </div>
              </div>
            ))}
            <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500">
              Total: {total}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
